import type { GameId } from './games';

/** yyyy-MM-dd in the user's local timezone — matches the iOS `dailyDateKey()`. */
export function todayKey(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

type ScheduleGames = Record<string, Record<string, unknown>>;

/** Raw `dailySchedule/{date}.quest` payload. */
export interface RawQuest {
  steps?: Array<{ gameId?: string; puzzle?: Record<string, unknown> }>;
  totalHints?: number;
  lives?: number;
  timePenaltySeconds?: number;
  coinsReward?: number;
}

interface ScheduleResponse {
  dateKey: string;
  isToday: boolean;
  games: ScheduleGames;
  quest: RawQuest | null;
}

// Session cache — avoids re-fetching when multiple components call the loaders.
let cachedPromise: Promise<ScheduleResponse | null> | null = null;
let cachedForDate: string | null = null;

/**
 * Prefer the Cloudflare Pages Function proxy (edge-cached, shielded Firestore
 * reads).
 *
 * Production always goes through the proxy — no direct browser-to-Firestore
 * reads. If the proxy is unreachable or errors, we return null rather than
 * fall back, so the attack surface is exactly the one /api/schedule endpoint.
 *
 * In local dev (`npm run dev`) Pages Functions don't run, so we fall back to
 * the Firebase SDK. Both the fallback branch and the SDK imports are gated
 * behind `import.meta.env.DEV`, which Vite replaces with a constant at build
 * time — Rollup tree-shakes the entire branch out of the production bundle,
 * so firebase/firestore never ships.
 */
async function fetchScheduleWithFallback(
  requestedDate: string,
): Promise<ScheduleResponse | null> {
  try {
    const r = await fetch(`/api/schedule?date=${encodeURIComponent(requestedDate)}`, {
      headers: { accept: 'application/json' },
    });
    if (r.ok) {
      const data = (await r.json()) as Partial<ScheduleResponse>;
      if (data && typeof data.dateKey === 'string') {
        return {
          dateKey: data.dateKey,
          isToday: Boolean(data.isToday),
          games: data.games ?? {},
          quest: data.quest ?? null,
        };
      }
    } else if (r.status === 404) {
      return null;
    }
    // Non-ok or malformed response → fall through.
  } catch {
    // Network error (dev, offline, or a Cloudflare outage) → fall through.
  }

  if (import.meta.env.DEV) {
    return fetchScheduleDirect(requestedDate);
  }
  return null;
}

async function fetchScheduleDirect(
  requestedDate: string,
): Promise<ScheduleResponse | null> {
  const [{ doc, getDoc }, { db }] = await Promise.all([
    import('firebase/firestore'),
    import('./firebase'),
  ]);

  // Probe exact English doc IDs, newest first — the same walk-back the Pages
  // Function does. A `__name__ desc` query needs an index that doesn't exist,
  // and would also hit the `{date}_{lang}` docs for other languages.
  const [y, m, d] = requestedDate.split('-').map((n) => parseInt(n, 10));
  for (let back = 0; back <= 30; back++) {
    const key = new Date(Date.UTC(y, m - 1, d - back)).toISOString().slice(0, 10);
    try {
      const snap = await getDoc(doc(db, 'dailySchedule', key));
      if (!snap.exists()) continue;
      const data = snap.data() as { games?: ScheduleGames; quest?: RawQuest };
      return {
        dateKey: key,
        isToday: back === 0,
        games: data.games ?? {},
        quest: data.quest ?? null,
      };
    } catch (e) {
      console.error('[daily] direct fetch failed', e);
      return null;
    }
  }
  return null;
}

async function getSchedule(): Promise<ScheduleResponse | null> {
  const today = todayKey();
  if (cachedPromise && cachedForDate === today) return cachedPromise;
  cachedForDate = today;
  cachedPromise = fetchScheduleWithFallback(today);
  return cachedPromise;
}

// Public API — same shapes as before, so the game components don't change.

export interface DailyPuzzle<T> {
  data: T;
  dateKey: string;
  isToday: boolean;
}

export async function loadTodaysPuzzle<T>(
  gameId: GameId,
): Promise<DailyPuzzle<T> | null> {
  const schedule = await getSchedule();
  if (!schedule) return null;
  const data = schedule.games[gameId] as T | undefined;
  if (!data) return null;
  return { data, dateKey: schedule.dateKey, isToday: schedule.isToday };
}

export async function loadTodaysScheduleSummary(): Promise<{
  dateKey: string;
  isToday: boolean;
  gameIds: Set<GameId>;
} | null> {
  const schedule = await getSchedule();
  if (!schedule) return null;
  return {
    dateKey: schedule.dateKey,
    isToday: schedule.isToday,
    gameIds: new Set(Object.keys(schedule.games) as GameId[]),
  };
}

export interface QuestStep {
  gameId: GameId;
  puzzle: Record<string, unknown>;
}

export interface DailyQuest {
  dateKey: string;
  isToday: boolean;
  steps: QuestStep[];
  totalHints: number;
  lives: number;
  timePenaltySeconds: number;
  coinsReward: number;
}

/**
 * Today's Quest Gauntlet. Mirrors `DailyQuest.parse` on iOS, including its
 * defaults: 3 hints, 3 lives, a 30-second wrong-answer penalty, 100 coins.
 */
export async function loadTodaysQuest(): Promise<DailyQuest | null> {
  const schedule = await getSchedule();
  if (!schedule?.quest) return null;

  const steps: QuestStep[] = [];
  for (const raw of schedule.quest.steps ?? []) {
    if (!raw?.gameId || !raw?.puzzle) continue;
    steps.push({ gameId: raw.gameId as GameId, puzzle: raw.puzzle });
  }
  if (!steps.length) return null;

  return {
    dateKey: schedule.dateKey,
    isToday: schedule.isToday,
    steps,
    totalHints: schedule.quest.totalHints ?? 3,
    lives: schedule.quest.lives ?? 3,
    timePenaltySeconds: schedule.quest.timePenaltySeconds ?? 30,
    coinsReward: schedule.quest.coinsReward ?? 100,
  };
}

/** Finishing under this many seconds earns the fast-completion bonus. */
export const QUEST_FAST_THRESHOLD_SECONDS = 16 * 60;
export const QUEST_FAST_BONUS_COINS = 100;

// Quest completion tracking. A failed quest stays locked until tomorrow's
// puzzle, exactly as on iOS.

const questDoneKey = (dateKey: string) => `quest:done:${dateKey}`;
const questFailedKey = (dateKey: string) => `quest:failed:${dateKey}`;

export interface QuestResult {
  totalElapsed: number;
  hintsUsed: number;
  livesLost: number;
  coinsEarned: number;
}

export function questResult(dateKey: string): QuestResult | null {
  const raw = localStorage.getItem(questDoneKey(dateKey));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as QuestResult;
  } catch {
    return null;
  }
}

export function markQuestCompleted(dateKey: string, result: QuestResult): void {
  localStorage.setItem(questDoneKey(dateKey), JSON.stringify(result));
}

export function isQuestFailed(dateKey: string): boolean {
  return localStorage.getItem(questFailedKey(dateKey)) === '1';
}

export function markQuestFailed(dateKey: string): void {
  localStorage.setItem(questFailedKey(dateKey), '1');
}

// Per-game daily completion now lives in `dailySession`, which tracks losses
// and in-progress state as well as wins. Quest state stays here.

/** Human-friendly "today" label: "Today, Apr 16" or "Apr 14 (latest)" for fallbacks. */
export function formatDateLabel(dateKey: string, isToday: boolean): string {
  const [y, m, d] = dateKey.split('-').map((n) => parseInt(n, 10));
  const date = new Date(y, m - 1, d);
  const formatted = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return isToday ? `Today, ${formatted}` : `${formatted} (latest)`;
}
